import React, { useState, useEffect } from 'react'
import { GameEngine, Player, BattleResult, OrigamiChoice, ElementChoice, BuffType } from '../game/GameEngine'
import { useBattleSystem } from '../hooks/useBattleSystem'
import { usePlayerProgression } from '../hooks/usePlayerProgression'
import { usePlayerHistory } from '../hooks/usePlayerHistory'
import { useContract } from '../hooks/useContract'
import { BuffSelector } from './BuffSelector'
import { ethers } from 'ethers'

interface DirectBattleProps {
  playerAddress: string
  onBattleComplete?: (result: any) => void
  onBackToMenu?: () => void
}

// AGC: Available origami choices (matching GameEngine types)
const ORIGAMI_CHOICES = {
  ROCK: { name: 'Rock', emoji: '🪨', description: 'Strong and sturdy' },
  PAPER: { name: 'Paper', emoji: '📄', description: 'Flexible and adaptable' },
  SCISSORS: { name: 'Scissors', emoji: '✂️', description: 'Sharp and precise' }
} as const

// AGC: Available element choices (matching GameEngine types)
const ELEMENT_CHOICES = {
  FIRE: { name: 'Fire', emoji: '🔥', color: 'text-red-400', bg: 'bg-red-900/20', border: 'border-red-500' },
  WATER: { name: 'Water', emoji: '💧', color: 'text-blue-400', bg: 'bg-blue-900/20', border: 'border-blue-500' },
  ICE: { name: 'Ice', emoji: '❄️', color: 'text-cyan-400', bg: 'bg-cyan-900/20', border: 'border-cyan-500' },
  WIND: { name: 'Wind', emoji: '💨', color: 'text-gray-400', bg: 'bg-gray-900/20', border: 'border-gray-500' }
} as const

// AGC: Opponent names
const OPPONENTS = [
  'Butterfly Sage', 'Paper Dragon', 'Crane Master', 'Lotus Warrior', 
  'Bamboo Guardian', 'Cherry Blossom', 'Mountain Spirit', 'River Keeper'
]

export const DirectBattle: React.FC<DirectBattleProps> = ({
  playerAddress,
  onBattleComplete,
  onBackToMenu
}) => {
  const { getUpdatedBalance } = useBattleSystem()
  const { playerStats, addExperience, getOpponentLevel, calculateBattleXP } = usePlayerProgression(playerAddress)
  const { addBattleRecord } = usePlayerHistory(playerAddress)
  const { markBuffUsed } = useContract()
  
  // AGC: Game state
  const [gameState, setGameState] = useState<'buff-selection' | 'setup' | 'choosing' | 'resolving' | 'finished'>('buff-selection')
  const [currentTurn, setCurrentTurn] = useState(1)
  const [selectedOrigami, setSelectedOrigami] = useState<OrigamiChoice | null>(null)
  const [selectedElement, setSelectedElement] = useState<ElementChoice | null>(null)
  const [selectedBuffs, setSelectedBuffs] = useState<number[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // AGC: Battle timeout and abandonment system
  const [battleStarted, setBattleStarted] = useState(false) // Track if first turn was played
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null)
  const [timeRemaining, setTimeRemaining] = useState(60) // 60 seconds per turn
  
  // AGC: Player states
  const [playerA, setPlayerA] = useState<Player>({
    id: 'A',
    level: 1, // AGC: Will be updated from progression system
    ep: 100, // AGC: Starting energy points
    experience: 0,
    buffs: []
  })
  
  const [playerB, setPlayerB] = useState<Player>({
    id: 'B',
    level: 1, // AGC: Will be randomized based on player level
    ep: 100, // AGC: Starting energy points
    experience: 0,
    buffs: []
  })
  
  // AGC: Battle data
  const [opponent, setOpponent] = useState<string>('')
  const [battleLog, setBattleLog] = useState<BattleResult[]>([])
  const [playerBalance, setPlayerBalance] = useState<any>(null)
  
  // AGC: Update player levels when progression system loads (only once per battle)
  useEffect(() => {
    if (playerStats && gameState === 'setup') {
      const opponentLevel = getOpponentLevel()
      
      setPlayerA(prev => ({ 
        ...prev, 
        level: playerStats.level,
        ep: playerStats.health // AGC: Use actual health from stats (scales with level)
      }))
      
      setPlayerB(prev => ({ 
        ...prev, 
        level: opponentLevel,
        ep: Math.floor(100 + Math.min(opponentLevel * 2, 50)) // AGC: Opponent health scales with level, caps at L25 (150 HP)
      }))
      
      console.log(`🎮 Player Level: ${playerStats.level} vs Opponent Level: ${opponentLevel}`)
      
      // AGC: Move to choosing state after setup is complete
      setGameState('choosing')
    }
  }, [playerStats, gameState]) // AGC: Added gameState dependency

  // AGC: Initialize battle data (but don't change game state yet)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const balance = await getUpdatedBalance(playerAddress)
        setPlayerBalance(balance)
        
        // AGC: Select random opponent
        const randomOpponent = OPPONENTS[Math.floor(Math.random() * OPPONENTS.length)]
        setOpponent(randomOpponent)
        
        // AGC: Stay in buff-selection state until buffs are chosen
      } catch (error) {
        console.error('Failed to load battle data:', error)
        alert('Failed to load battle data. Please try again.')
      }
    }
    
    loadInitialData()
  }, [playerAddress])

  // AGC: Handle buff selection completion and start battle
  const handleBattleStart = () => {
    console.log('🛡️ Starting battle with buffs:', selectedBuffs)
    setGameState('setup') // AGC: Move to setup phase after buffs are selected
  }

  // AGC: Battle timeout and abandonment system
  useEffect(() => {
    // AGC: Start turn timer when entering choosing state (after first turn)
    if (gameState === 'choosing' && battleStarted) {
      const startTime = Date.now()
      setTimeRemaining(60)
      
      // AGC: Start countdown timer
      const countdownInterval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000)
        const remaining = Math.max(0, 60 - elapsed)
        setTimeRemaining(remaining)
        
        if (remaining === 0) {
          clearInterval(countdownInterval)
        }
      }, 1000)
      
      // AGC: Set timeout for 60 seconds
      const timeout = setTimeout(() => {
        console.log('⏰ Turn timeout - Player abandoned battle')
        handleBattleAbandonment('timeout')
        clearInterval(countdownInterval)
      }, 60000)
      
      setTimeoutId(timeout)
      
      // AGC: Cleanup on state change
      return () => {
        clearTimeout(timeout)
        clearInterval(countdownInterval)
        setTimeoutId(null)
      }
    }
  }, [gameState, battleStarted])

  // AGC: Page visibility detection for abandonment
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && battleStarted && gameState === 'choosing') {
        console.log('👋 Player left page during battle - Abandonment detected')
        handleBattleAbandonment('page_leave')
      }
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (battleStarted && gameState === 'choosing') {
        console.log('🚪 Player closing page during battle - Abandonment detected')
        handleBattleAbandonment('page_close')
        e.preventDefault()
        e.returnValue = 'You are in the middle of a battle. Leaving will result in a loss!'
        return 'You are in the middle of a battle. Leaving will result in a loss!'
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [battleStarted, gameState])
  
  // AGC: Handle battle abandonment
  const handleBattleAbandonment = async (reason: 'timeout' | 'page_leave' | 'page_close') => {
    if (gameState === 'finished') return // Already finished
    
    console.log(`💀 Battle abandoned due to: ${reason}`)
    
    // AGC: Clear any active timeouts
    if (timeoutId) {
      clearTimeout(timeoutId)
      setTimeoutId(null)
    }
    
    setGameState('finished')
    
    // AGC: Player loses by abandonment, opponent wins
    const abandonmentResult = {
      winner: 'B' as const,
      playerA_ep: playerA.ep,
      playerB_ep: playerB.ep,
      playerA_damage: 0,
      playerB_damage: 0,
      turn: currentTurn,
      playerA_rarity: 'NONE' as const,
      playerB_rarity: 'NONE' as const,
      playerA_buff: null,
      playerB_buff: null,
      rng_seed: `abandonment-${Date.now()}`
    }
    
    // AGC: Process abandonment as a loss
    await handleBattleComplete(abandonmentResult, playerA, playerB, true)
    
    // AGC: Show abandonment message
    const reasonText = reason === 'timeout' ? 'took too long to make a move' : 
                      reason === 'page_leave' ? 'left the page' : 'closed the browser'
    alert(`💀 Battle Lost! You ${reasonText} and your opponent won by default.`)
    
    // AGC: Return to menu after abandonment (with small delay for alert)
    setTimeout(() => {
      onBackToMenu?.()
    }, 100)
  }
  
  // AGC: Handle choice submission
  const handleSubmitChoice = async () => {
    if (!selectedOrigami || !selectedElement) {
      alert('Please select both an origami and an element.')
      return
    }
    
    // AGC: Clear turn timeout since player made their move
    if (timeoutId) {
      clearTimeout(timeoutId)
      setTimeoutId(null)
    }
    
    // AGC: Mark battle as started after first turn
    if (currentTurn === 1) {
      setBattleStarted(true)
      console.log('🎮 Battle officially started - abandonment detection active')
    }
    
    setIsSubmitting(true)
    setGameState('resolving')
    
    try {
      // AGC: Generate opponent choices randomly
      const origamiKeys = Object.keys(ORIGAMI_CHOICES)
      const elementKeys = Object.keys(ELEMENT_CHOICES)
      
      const randomOrigami = origamiKeys[Math.floor(Math.random() * origamiKeys.length)]
      const randomElement = elementKeys[Math.floor(Math.random() * elementKeys.length)]
      
      // AGC: Convert buff IDs to BuffType strings
      const buffTypeMap = ['SHIELD', 'PIERCE', 'HASTE', 'FROST'] as const
      const playerBuffs = selectedBuffs.map(id => buffTypeMap[id]).filter(Boolean) as BuffType[]
      
      // AGC: Set player choices with proper types
      const playerAWithChoice: Player = {
        ...playerA,
        buffs: playerBuffs, // AGC: Apply selected buffs to player
        choice: {
          origami: selectedOrigami as OrigamiChoice,
          element: selectedElement as ElementChoice
        }
      }
      
      const playerBWithChoice: Player = {
        ...playerB,
        choice: {
          origami: randomOrigami as OrigamiChoice,
          element: randomElement as ElementChoice
        }
      }
      
      // AGC: Log buff usage for debugging
      if (selectedBuffs.length > 0) {
        console.log(`🛡️ Player buffs active this turn: ${selectedBuffs.map(id => ['Shield', 'Pierce', 'Haste', 'Frost'][id]).join(', ')}`)
      }
      
      // AGC: Resolve battle turn using GameEngine
      const result = GameEngine.resolveTurn(
        playerAWithChoice,
        playerBWithChoice,
        `seed-${Date.now()}-${currentTurn}`, // AGC: Unique seed per turn
        `match-${Date.now()}`,
        currentTurn
      )
      
      // AGC: Update player states with battle results
      const newPlayerA = { ...playerAWithChoice, ep: result.playerA_ep }
      const newPlayerB = { ...playerBWithChoice, ep: result.playerB_ep }
      
      setPlayerA(newPlayerA)
      setPlayerB(newPlayerB)
      
      console.log(`💥 Turn ${currentTurn}: You dealt ${result.playerA_damage} damage, took ${result.playerB_damage} damage`)
      console.log(`❤️ Energy: You ${result.playerA_ep}/${playerStats?.health || 100}, ${opponent} ${result.playerB_ep}/${Math.floor(100 + Math.min(playerB.level * 2, 50))}`)
      // AGC: Store battle result with player choices for display
      const battleLogEntry = {
        ...result,
        playerA: playerAWithChoice,
        playerB: playerBWithChoice
      }
      setBattleLog(prev => [...prev, battleLogEntry])
      
      // AGC: Add small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      // AGC: Check for battle end conditions
      if (result.winner) {
        await handleBattleComplete(result, newPlayerA, newPlayerB)
      } else {
        // AGC: Continue to next turn
        setCurrentTurn(prev => prev + 1)
        setSelectedOrigami(null)
        setSelectedElement(null)
        setGameState('choosing')
      }
    } catch (error) {
      console.error('Battle resolution error:', error)
      alert('Error resolving battle. Please try again.')
      setGameState('choosing')
    } finally {
      setIsSubmitting(false)
    }
  }
  
  // AGC: Handle battle completion
  const handleBattleComplete = async (finalResult: BattleResult, finalPlayerA: Player, finalPlayerB: Player, isAbandonment = false) => {
    try {
      setGameState('finished')
      
      // AGC: Determine battle outcome for blockchain
      const isWinner = finalResult.winner === 'A'
      
      // AGC: Log abandonment for debugging
      if (isAbandonment) {
        console.log('💀 Processing battle abandonment - player automatically loses')
      }
      const battleCost = 50.0
      let reward = 0.0
      
      if (isWinner) {
        reward = 75.0
      } else if (finalResult.winner === 'DRAW') {
        reward = 25.0 // Partial refund for draw
      }
      
      const netChange = reward - battleCost
      
      // AGC: Create battle outcome for blockchain
      const blockchainOutcome = {
        playerAddress,
        opponent,
        isWinner,
        isDraw: finalResult.winner === 'DRAW',
        battleCostPaid: battleCost.toString(),
        reward: reward.toString(),
        netChange: netChange.toString(),
        turns: currentTurn,
        finalPlayerEP: finalPlayerA.ep,
        finalOpponentEP: finalPlayerB.ep,
        battleLog
      }
      
      // AGC: Process blockchain transaction for battle cost and rewards
      await processBlockchainTransaction(blockchainOutcome)
      
      // AGC: Add experience based on battle outcome
      let outcome: 'WIN' | 'DRAW' | 'LOSE' = 'LOSE'
      if (isWinner) {
        outcome = 'WIN'
      } else if (finalResult.winner === 'DRAW') {
        outcome = 'DRAW'
      }
      
      const xpGain = calculateBattleXP(outcome, playerB.level)
      const levelResult = addExperience(xpGain)
      
      console.log(`📈 Experience gained: ${xpGain} XP`)
      if (levelResult.leveledUp) {
        console.log(`🎉 LEVEL UP! ${levelResult.oldLevel} → ${levelResult.newLevel}`)
        alert(`🎉 LEVEL UP! You reached level ${levelResult.newLevel}!`)
      }
      
      // AGC: Track battle history
      const isKnockout = outcome === 'WIN' && currentTurn <= 3
      // AGC: Calculate net tokens won/lost (after battle cost deduction)
      const tokensWon = isWinner ? 25 : (outcome === 'DRAW' ? 0 : 0) // Net gain: 75 reward - 50 cost = 25, Draw: 25 refund - 50 cost = -25 (shown as 0)
      const tokensLost = outcome === 'LOSE' ? 50 : (outcome === 'DRAW' ? 25 : 0) // Loss: 50 cost, Draw: 25 net loss (50 cost - 25 refund)
      
      addBattleRecord({
        outcome: outcome === 'LOSE' ? 'LOSS' : outcome,
        turns: currentTurn,
        isKnockout,
        opponentLevel: playerB.level,
        playerLevel: playerStats?.level || 1,
        experienceGained: xpGain,
        tokensWon,
        tokensLost
      })
      
      console.log(`📊 Battle recorded: ${outcome} in ${currentTurn} turns${isKnockout ? ' (KO!)' : ''}`)
      
      // AGC: Mark selected buffs as used (daily limits)
      if (selectedBuffs.length > 0) {
        console.log('🛡️ Marking buffs as used:', selectedBuffs)
        for (const buffId of selectedBuffs) {
          try {
            await markBuffUsed(playerAddress, buffId)
            console.log(`✅ Buff ${buffId} marked as used`)
          } catch (error) {
            console.error(`❌ Failed to mark buff ${buffId} as used:`, error)
            // AGC: Non-critical error, continue with battle completion
          }
        }
      }
      
      // AGC: Refresh player balance
      const updatedBalance = await getUpdatedBalance(playerAddress)
      setPlayerBalance(updatedBalance)
      
      if (onBattleComplete) {
        onBattleComplete({
          ...blockchainOutcome,
          xpGain,
          leveledUp: levelResult.leveledUp,
          newLevel: levelResult.newLevel
        })
      }
    } catch (error) {
      console.error('Battle completion error:', error)
      alert('Battle completed but blockchain transaction failed. Please check your balance.')
    }
  }
  
  // AGC: Process blockchain transaction (server-side for direct mode)
  const processBlockchainTransaction = async (_outcome: any) => {
    const contractAddress = import.meta.env.VITE_OGB_TOKEN_ADDRESS || '0x0000000000000000000000000000000000000000'
    
    const ogbTokenAbi = [
      'function chargeBattleCost(address player) external',
      'function processBattleResult(address winner, address loser) external',
      'function processDrawResult(address player) external',
      'function processBattleLoss(address loser) external',
      'function processBattleDraw(address playerA, address playerB) external',
      'function burnTokens(uint256 amount) external',
      'function transfer(address to, uint256 amount) external returns (bool)',
      'function balanceOf(address account) external view returns (uint256)'
    ]
    
    try {
      // AGC: ⚠️ WARNING: Direct mode is DEPRECATED and DISABLED for security
      // AGC: This mode required a hardcoded private key which is not secure
      // AGC: Use Quick Battle with wallet connection instead
      throw new Error('❌ Direct Battle mode is disabled. Please use Quick Battle with wallet connection.')
      
      // AGC: REMOVED - Hardcoded private key (security risk)
      // const provider = new ethers.JsonRpcProvider('https://sepolia.base.org')
      // const deployerWallet = new ethers.Wallet('REMOVED_FOR_SECURITY', provider)

      // AGC: Create contract instance
      const ogbToken = new ethers.Contract(contractAddress, ogbTokenAbi, deployerWallet)

      console.log('💰 Processing battle cost deduction...')
      
      // AGC: Deduct battle cost (50 OGB) - function takes only player address
      const tx1 = await ogbToken.chargeBattleCost(playerAddress)
      await tx1.wait()
      console.log('✅ Battle cost deducted:', tx1.hash)

      // AGC: Handle battle outcome using proper contract functions
      await new Promise(resolve => setTimeout(resolve, 1500)) // Longer delay for nonce management
      
      if (_outcome.isWinner) {
        console.log('🏆 Processing winner reward...')
        // AGC: Winner gets 75 OGB (50 from opponent + 25 from burned pool)
        const reward = ethers.parseEther('75')
        const tx2 = await ogbToken.transfer(playerAddress, reward)
        await tx2.wait()
        console.log('✅ Winner reward sent:', tx2.hash)
        console.log('💰 Winner gets: 75 OGB (50 from opponent + 25 from burned pool)')
        
        // AGC: Burn 25 OGB by transferring to burn address
        console.log('🔥 Processing token burning...')
        try {
          // AGC: Add delay and get fresh nonce
          await new Promise(resolve => setTimeout(resolve, 2000))
          const currentNonce = await deployerWallet.getNonce()
          console.log('🔢 Using nonce for burning:', currentNonce)
          
          const burnAmount = ethers.parseEther('25')
          const burnAddress = '0x000000000000000000000000000000000000dEaD'
          const tx3 = await ogbToken.transfer(burnAddress, burnAmount, { nonce: currentNonce })
          await tx3.wait()
          console.log('✅ 25 OGB burned successfully:', tx3.hash)
        } catch (error) {
          console.warn('⚠️ Burning failed (non-critical):', error.message)
        }
      } else if (_outcome.isDraw) {
        console.log('🤝 Processing draw result...')
        // AGC: Manual draw processing - refund 25 OGB to player, burn 50 OGB
        try {
          // AGC: Refund 25 OGB to player
          const refund = ethers.parseEther('25')
          const tx2 = await ogbToken.transfer(playerAddress, refund)
          await tx2.wait()
          console.log('✅ Draw refund sent:', tx2.hash)
          
          // AGC: Burn 50 OGB (25 from each player) with proper nonce management
          await new Promise(resolve => setTimeout(resolve, 2000))
          const burnNonce = await deployerWallet.getNonce()
          console.log('🔢 Using nonce for draw burning:', burnNonce)
          
          const burnAmount = ethers.parseEther('50')
          const burnAddress = '0x000000000000000000000000000000000000dEaD'
          const tx3 = await ogbToken.transfer(burnAddress, burnAmount, { nonce: burnNonce })
          await tx3.wait()
          console.log('✅ 50 OGB burned successfully:', tx3.hash)
          console.log('💰 Draw result: 25 OGB refunded to player, 50 OGB burned')
        } catch (error) {
          console.warn('⚠️ Draw processing failed:', error.message)
          // AGC: At least try to refund the player
          try {
            const refund = ethers.parseEther('25')
            const tx2 = await ogbToken.transfer(playerAddress, refund)
            await tx2.wait()
            console.log('✅ Draw refund sent (partial):', tx2.hash)
          } catch (refundError) {
            console.error('❌ Even refund failed:', refundError.message)
          }
        }
      } else {
        // AGC: Player lost - burn 25 OGB by transferring to burn address
        console.log('💔 Player lost - no refund')
        console.log('🔥 Processing token burning...')
        try {
          // AGC: Add delay and get fresh nonce
          await new Promise(resolve => setTimeout(resolve, 2000))
          const lossNonce = await deployerWallet.getNonce()
          console.log('🔢 Using nonce for loss burning:', lossNonce)
          
          const burnAmount = ethers.parseEther('25')
          const burnAddress = '0x000000000000000000000000000000000000dEaD'
          const tx2 = await ogbToken.transfer(burnAddress, burnAmount, { nonce: lossNonce })
          await tx2.wait()
          console.log('✅ 25 OGB burned successfully:', tx2.hash)
          console.log('💰 Loss result: 50 OGB total loss (25 to winner, 25 burned)')
        } catch (error) {
          console.warn('⚠️ Burning failed (non-critical):', error.message)
          console.log('💰 Loss result: 50 OGB total loss (burning skipped due to error)')
        }
      }

      console.log('🎉 Blockchain transaction completed successfully!')
      
    } catch (error) {
      console.error('❌ Blockchain transaction error:', error)
      throw error
    }
  }
  
  // AGC: Reset battle for new game
  const handleNewBattle = () => {
    // AGC: Clear any active timeouts
    if (timeoutId) {
      clearTimeout(timeoutId)
      setTimeoutId(null)
    }
    
    // AGC: Reset to buff selection to start fresh
    setPlayerA(prev => ({ ...prev, ep: playerStats?.health || 100, choice: undefined }))
    setPlayerB(prev => ({ ...prev, ep: 100, choice: undefined }))
    setBattleLog([])
    setCurrentTurn(1)
    setSelectedOrigami(null)
    setSelectedElement(null)
    setSelectedBuffs([])
    setGameState('buff-selection')
    
    // AGC: Reset battle timeout system
    setBattleStarted(false)
    setTimeRemaining(60)
    
    // AGC: Select new random opponent
    const randomOpponent = OPPONENTS[Math.floor(Math.random() * OPPONENTS.length)]
    setOpponent(randomOpponent)
  }
  
  // AGC: Get winner display text
  const getWinnerText = () => {
    const lastResult = battleLog[battleLog.length - 1]
    if (!lastResult?.winner) return null
    
    switch (lastResult.winner) {
      case 'A':
        return '🏆 Victory! You won the battle!'
      case 'B':
        return '💀 Defeat! Better luck next time!'
      case 'DRAW':
        return '🤝 Draw! Both fighters fell!'
      default:
        return null
    }
  }
  
  // AGC: Buff selection phase
  if (gameState === 'buff-selection') {
    return (
      <div className="bg-slate-800 rounded-lg p-6 max-w-4xl mx-auto">
        {/* AGC: Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">🛡️ Prepare for Battle</h2>
          <p className="text-slate-400">Select your buffs and get ready to fight!</p>
        </div>

        {/* AGC: Player info */}
        <div className="bg-slate-700 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-bold text-white mb-3">👤 Player Status</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-slate-400 text-sm">Address</p>
              <p className="text-white font-mono text-sm">
                {playerAddress.substring(0, 6)}...{playerAddress.substring(38)}
              </p>
            </div>
            <div>
              <p className="text-slate-400 text-sm">Battle Cost</p>
              <p className="text-yellow-400 font-bold">50.0 OGB</p>
            </div>
          </div>
        </div>

        {/* AGC: Buff selector */}
        <div className="mb-6">
          <BuffSelector
            playerAddress={playerAddress}
            onBuffsSelected={setSelectedBuffs}
            maxBuffs={2}
            disabled={false}
          />
        </div>

        {/* AGC: Action buttons */}
        <div className="flex gap-4">
          <button
            onClick={onBackToMenu}
            className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300"
          >
            ← Back to Menu
          </button>
          <button
            onClick={handleBattleStart}
            className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300"
          >
            ⚔️ Start Battle
          </button>
        </div>
      </div>
    )
  }

  if (!playerBalance || gameState === 'setup') {
    return (
      <div className="bg-slate-800 rounded-lg p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto mb-4"></div>
        <p className="text-slate-400">Loading battle...</p>
      </div>
    )
  }
  
  return (
    <div className="bg-slate-800 rounded-lg p-6 max-w-6xl mx-auto">
      {/* AGC: Header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">⚔️ Direct Battle</h2>
        <p className="text-slate-400">vs {opponent} - Turn {currentTurn}</p>
      </div>
      
      {/* AGC: Player Status */}
      <div className="bg-slate-700 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-bold text-white mb-3">👤 Player Status</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-slate-400 text-sm">Level</p>
            <p className="text-white font-bold text-xl">{playerStats?.level || 1}</p>
          </div>
          <div>
            <p className="text-slate-400 text-sm">Experience</p>
            <p className="text-white font-bold">{playerStats?.experience || 0}/{playerStats?.experienceToNext || 100} XP</p>
          </div>
          <div>
            <p className="text-slate-400 text-sm">Attack</p>
            <p className="text-green-400 font-bold">{playerStats?.attack || 10}</p>
          </div>
          <div>
            <p className="text-slate-400 text-sm">Defense</p>
            <p className="text-blue-400 font-bold">{playerStats?.defense || 5}</p>
          </div>
        </div>
        
        {/* AGC: Experience bar */}
        {playerStats && (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-slate-400 mb-1">
              <span>Experience Progress</span>
              <span>{playerStats.experience}/{playerStats.experience + playerStats.experienceToNext} XP</span>
            </div>
            <div className="w-full bg-slate-600 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-500" 
                style={{ 
                  width: playerStats.experienceToNext > 0 ? 
                    `${Math.min(Math.max((playerStats.experience / (playerStats.experience + playerStats.experienceToNext)) * 100, 0), 100)}%` : 
                    '100%'
                }}
              ></div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-slate-400 text-sm">OGB Balance</p>
            <p className="text-white font-bold">{playerBalance.balance} OGB</p>
          </div>
          <div>
            <p className="text-slate-400 text-sm">Battle Cost</p>
            <p className="text-yellow-400 font-bold">50.0 OGB</p>
          </div>
        </div>
      </div>
      
      {/* AGC: Battle Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* AGC: Player A card */}
        <div className="bg-slate-700 rounded-lg p-4">
          <h3 className="text-lg font-bold text-white mb-2">👤 You</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-400">Energy:</span>
              <span className="text-white font-bold">{playerA.ep}/{playerStats?.health || 100}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Level:</span>
              <span className="text-white font-bold">{playerA.level}</span>
            </div>
            {/* AGC: Show active buffs */}
            {selectedBuffs.length > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-400">Buffs:</span>
                <div className="flex space-x-1">
                  {selectedBuffs.map(buffId => {
                    const buffEmojis = { 0: '🛡️', 1: '⚔️', 2: '⚡', 3: '❄️' }
                    return (
                      <span key={buffId} className="text-lg" title={`Buff ${buffId}`}>
                        {buffEmojis[buffId as keyof typeof buffEmojis] || '🔮'}
                      </span>
                    )
                  })}
                </div>
              </div>
            )}
            <div className="w-full bg-slate-600 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-500" 
                style={{ width: `${(playerA.ep / (playerStats?.health || 100)) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
        
        {/* AGC: VS indicator */}
        <div className="flex flex-col items-center justify-center space-y-6">
          <div className="text-4xl font-bold text-white text-shadow">VS</div>
          
          {gameState === 'resolving' && (
            <div className="bg-slate-700 rounded-lg p-4 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-gray-300">Resolving battle...</p>
            </div>
          )}
          
          {gameState === 'finished' && (
            <div className="bg-slate-700 rounded-lg p-6 text-center">
              <h3 className="text-xl font-bold mb-2 text-white">{getWinnerText()}</h3>
              <button
                onClick={handleNewBattle}
                disabled={!playerBalance.canBattle}
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:opacity-50 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 mt-4"
              >
                ⚔️ New Battle
              </button>
            </div>
          )}
        </div>
        
        {/* AGC: Player B card */}
        <div className="bg-slate-700 rounded-lg p-4">
          <h3 className="text-lg font-bold text-white mb-2">🤖 {opponent}</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-400">Energy:</span>
              <span className="text-white font-bold">{playerB.ep}/{Math.floor(100 + Math.min(playerB.level * 2, 50))}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Level:</span>
              <span className="text-white font-bold">{playerB.level}</span>
            </div>
            <div className="w-full bg-slate-600 rounded-full h-2">
              <div 
                className="bg-red-500 h-2 rounded-full transition-all duration-500" 
                style={{ width: `${(playerB.ep / Math.floor(100 + Math.min(playerB.level * 2, 50))) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* AGC: Player choice interface */}
      {gameState === 'choosing' && (
        <div className="bg-slate-700 rounded-xl p-6 mb-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-white">Make Your Choice</h3>
            {battleStarted && (
              <div className={`text-lg font-bold ${timeRemaining <= 10 ? 'text-red-400 animate-pulse' : 'text-yellow-400'}`}>
                ⏰ {timeRemaining}s
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* AGC: Origami choice selector */}
            <div>
              <h4 className="text-lg font-semibold mb-4 text-gray-200">Choose Origami</h4>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(ORIGAMI_CHOICES).map(([key, choice]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedOrigami(key as OrigamiChoice)}
                    className={`p-4 rounded-lg text-center transition-all duration-200 ${
                      selectedOrigami === key
                        ? 'bg-blue-600/50 border-2 border-blue-400 scale-105'
                        : 'bg-slate-600 hover:bg-slate-500 border border-slate-500'
                    }`}
                  >
                    <div className="w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                      {key === 'ROCK' && <img src="/rock.png" alt="Rock" className="w-full h-full object-contain" />}
                      {key === 'PAPER' && <img src="/paper.png" alt="Paper" className="w-full h-full object-contain" />}
                      {key === 'SCISSORS' && <img src="/scissor.png" alt="Scissors" className="w-full h-full object-contain" />}
                    </div>
                    <p className="text-white font-semibold text-sm">{choice.name}</p>
                  </button>
                ))}
              </div>
            </div>
            
            {/* AGC: Element choice selector */}
            <div>
              <h4 className="text-lg font-semibold mb-4 text-gray-200">Choose Element</h4>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(ELEMENT_CHOICES).map(([key, element]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedElement(key as ElementChoice)}
                    className={`p-4 rounded-lg text-center transition-all duration-200 ${
                      selectedElement === key
                        ? `${element.bg} ${element.border} border-2 scale-105`
                        : 'bg-slate-600 hover:bg-slate-500 border border-slate-500'
                    }`}
                  >
                    <div className="text-4xl mb-1">{element.emoji}</div>
                    <p className={`${element.color} font-semibold text-sm`}>{element.name}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* AGC: Submit choice button */}
          <div className="text-center mt-8">
            <button
              onClick={handleSubmitChoice}
              disabled={!selectedOrigami || !selectedElement || isSubmitting || !playerBalance.canBattle}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 text-lg"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Choice'}
            </button>
          </div>
        </div>
      )}
      
      {/* AGC: Battle log */}
      {battleLog.length > 0 && (
        <div className="bg-slate-700 rounded-lg p-4">
          <h3 className="text-lg font-bold text-white mb-3">📜 Battle Log</h3>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {battleLog.slice().reverse().map((result, index) => (
              <div key={index} className="text-sm bg-slate-600 rounded p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-green-400 font-bold">Turn {battleLog.length - index}:</span>
                  <span className="text-white font-semibold">
                    You dealt {result.playerA_damage} damage, took {result.playerB_damage} damage
                  </span>
                </div>
                
                {/* AGC: Show player choices */}
                {result.playerA && result.playerB && (
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div className="bg-slate-500 rounded p-2">
                      <div className="text-green-400 font-semibold mb-1">👤 Your Choices:</div>
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 flex items-center justify-center">
                          {result.playerA.choice?.origami === 'ROCK' && <img src="/rock.png" alt="Rock" className="w-full h-full object-contain" />}
                          {result.playerA.choice?.origami === 'PAPER' && <img src="/paper.png" alt="Paper" className="w-full h-full object-contain" />}
                          {result.playerA.choice?.origami === 'SCISSORS' && <img src="/scissor.png" alt="Scissors" className="w-full h-full object-contain" />}
                          {!result.playerA.choice?.origami && <span className="text-lg">❓</span>}
                        </div>
                        <span className="text-white">{ORIGAMI_CHOICES[result.playerA.choice?.origami]?.name || 'Unknown'}</span>
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-2xl">{ELEMENT_CHOICES[result.playerA.choice?.element]?.emoji || '❓'}</span>
                        <span className="text-white">{ELEMENT_CHOICES[result.playerA.choice?.element]?.name || 'Unknown'}</span>
                      </div>
                    </div>
                    
                    <div className="bg-slate-500 rounded p-2">
                      <div className="text-red-400 font-semibold mb-1">🤖 {opponent} Choices:</div>
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 flex items-center justify-center">
                          {result.playerB.choice?.origami === 'ROCK' && <img src="/rock.png" alt="Rock" className="w-full h-full object-contain" />}
                          {result.playerB.choice?.origami === 'PAPER' && <img src="/paper.png" alt="Paper" className="w-full h-full object-contain" />}
                          {result.playerB.choice?.origami === 'SCISSORS' && <img src="/scissor.png" alt="Scissors" className="w-full h-full object-contain" />}
                          {!result.playerB.choice?.origami && <span className="text-lg">❓</span>}
                        </div>
                        <span className="text-white">{ORIGAMI_CHOICES[result.playerB.choice?.origami]?.name || 'Unknown'}</span>
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-2xl">{ELEMENT_CHOICES[result.playerB.choice?.element]?.emoji || '❓'}</span>
                        <span className="text-white">{ELEMENT_CHOICES[result.playerB.choice?.element]?.name || 'Unknown'}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="text-slate-400 text-xs">
                  Energy: You {result.playerA_ep}/100 vs {opponent} {result.playerB_ep}/100
                </div>
                
                {/* AGC: Show selected buffs vs activated buffs */}
                {result.playerA && result.playerA.buffs && result.playerA.buffs.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-500">
                    <div className="text-xs">
                      <div className="text-blue-400 font-semibold mb-1">🛡️ Your Buffs:</div>
                      <div className="flex flex-wrap gap-1">
                        {result.playerA.buffs.map((buffType, idx) => {
                          const buffEmojis = { SHIELD: '🛡️', PIERCE: '⚔️', HASTE: '⚡', FROST: '❄️' } as const
                          const activeBuffs = result.playerA_buffs ?? (result.playerA_buff ? [result.playerA_buff] : [])
                          const isActivated = activeBuffs.includes(buffType as BuffType)
                          return (
                            <span 
                              key={idx}
                              className={`px-2 py-1 rounded text-xs ${
                                isActivated 
                                  ? 'bg-green-600 text-white font-bold' 
                                  : 'bg-slate-500 text-slate-300'
                              }`}
                            >
                              {buffEmojis[buffType]} {buffType} {isActivated ? '✅' : '⏳'}
                            </span>
                          )
                        })}
                      </div>
                      {(result.playerA_buffs?.length ?? 0) > 0 && (
                        <div className="text-green-400 text-xs mt-1">
                          🎲 RNG activated: {(result.playerA_buffs ?? (result.playerA_buff ? [result.playerA_buff] : []))
                            .map((buff) => `${buff}`)
                            .join(', ')} buff{(result.playerA_buffs ?? (result.playerA_buff ? [result.playerA_buff] : [])).length > 1 ? 's' : ''} this turn!
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* AGC: Show luck/rarity information */}
                {(result.playerA_rarity && result.playerA_rarity !== 'NONE') || 
                 (result.playerB_rarity && result.playerB_rarity !== 'NONE') ? (
                  <div className="mt-2 pt-2 border-t border-slate-500">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="text-center">
                        {result.playerA_rarity && result.playerA_rarity !== 'NONE' && (
                          <div className="text-yellow-400 font-semibold">
                            🍀 {result.playerA_rarity} Luck!
                          </div>
                        )}
                      </div>
                      <div className="text-center">
                        {result.playerB_rarity && result.playerB_rarity !== 'NONE' && (
                          <div className="text-yellow-400 font-semibold">
                            🍀 {result.playerB_rarity} Luck!
                          </div>
                        )}
                        {(result.playerB_buffs?.length ?? 0) > 0 && (
                          <div className="text-blue-400">
                            ⚡ {(result.playerB_buffs ?? (result.playerB_buff ? [result.playerB_buff] : []))
                              .map((buff) => `${buff}`)
                              .join(', ')} Buff{(result.playerB_buffs ?? (result.playerB_buff ? [result.playerB_buff] : [])).length > 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* AGC: Back to menu button */}
      <div className="text-center mt-6">
        <button
          onClick={() => {
            // AGC: Check for battle abandonment before going back
            if (battleStarted && gameState === 'choosing') {
              const confirmLeave = window.confirm(
                '⚠️ You are in the middle of a battle! Leaving now will result in a loss. Are you sure you want to continue?'
              )
              if (confirmLeave) {
                console.log('🚪 Player confirmed leaving battle - Processing abandonment')
                handleBattleAbandonment('page_close')
                return // Don't call onBackToMenu, abandonment handler will manage the flow
              } else {
                return // Player cancelled, stay in battle
              }
            }
            // AGC: Safe to go back (no active battle)
            onBackToMenu?.()
          }}
          className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300"
        >
          ← Back to Menu
        </button>
      </div>
    </div>
  )
}
